import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, request } from '../api/client';

interface LinkConfig {
  whatsappLink: string;
  kiwifyMembersLink: string;
  linkTiragemAvulsa: string;
  supportEmail: string;
  emailFromName: string;
  signatureName: string;
  signatureRole: string;
  tiragemValorCentavos: number;
  moeda: string;
  frontCheckoutLink: string;
  updatedAt: string;
  updatedBy: string | null;
}

type EditableLinkConfig = Omit<LinkConfig, 'updatedAt' | 'updatedBy'>;

export function ConfigPage(): JSX.Element {
  return (
    <section aria-labelledby="config-heading" className="space-y-6 max-w-3xl">
      <header>
        <h1 id="config-heading" className="h1">
          Configurações
        </h1>
        <p className="muted text-sm mt-1">
          Links e textos editáveis sem precisar de novo deploy. Refletidos no template de email de
          boas-vindas e em outras peças do produto.
        </p>
      </header>
      <LinkConfigSection />
      <EmailPreviewSection />
      <div className="card">
        <h2 className="text-lg font-semibold mb-2">Chaves de API e secrets</h2>
        <p className="text-sm text-sara-text-muted">
          Continuam em Docker Swarm secrets + env vars do stack. Edição inline no painel (override
          no banco com criptografia) entra em refinamento futuro.
        </p>
      </div>
    </section>
  );
}

interface EmailPreviewResponse {
  kind: 'front' | 'upsell';
  subject: string;
  html: string;
}

function EmailPreviewSection(): JSX.Element {
  const [openKind, setOpenKind] = useState<'front' | 'upsell' | null>(null);
  const [primeiroNome, setPrimeiroNome] = useState('Maria');
  const [token, setToken] = useState('SE-A3F9K2');

  const preview = useQuery<EmailPreviewResponse>({
    queryKey: ['email-preview', openKind, primeiroNome, token],
    queryFn: () =>
      request<EmailPreviewResponse>(
        `/admin/links-config/email-preview?kind=${openKind}&primeiroNome=${encodeURIComponent(
          primeiroNome,
        )}&token=${encodeURIComponent(token)}`,
      ),
    enabled: openKind !== null,
    staleTime: 0,
  });

  return (
    <div className="card space-y-3">
      <div>
        <h2 className="text-lg font-semibold">Preview do email de boas-vindas</h2>
        <p className="muted text-xs mt-1">
          Renderiza o HTML do email com os links e assinatura ATUAIS + um token de exemplo. Não
          envia nada — só mostra como ficaria.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="block text-sm font-medium text-sara-text">Nome de exemplo</span>
          <input
            type="text"
            value={primeiroNome}
            onChange={(e) => setPrimeiroNome(e.target.value)}
            className="input text-sm w-full"
            maxLength={40}
          />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-sara-text">Token de exemplo</span>
          <input
            type="text"
            value={token}
            onChange={(e) => setToken(e.target.value.toUpperCase())}
            className="input text-sm w-full font-mono"
            placeholder="SE-A3F9K2"
            maxLength={9}
          />
        </label>
      </div>

      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn-ghost text-sm" onClick={() => setOpenKind('front')}>
          Preview email front (Semente Energética)
        </button>
        <button type="button" className="btn-ghost text-sm" onClick={() => setOpenKind('upsell')}>
          Preview email upsell (Mapa Astral)
        </button>
      </div>

      {openKind !== null && (
        <PreviewModal
          response={preview.data}
          isLoading={preview.isLoading}
          isError={preview.isError}
          error={preview.error}
          onClose={() => setOpenKind(null)}
        />
      )}
    </div>
  );
}

interface PreviewModalProps {
  response: EmailPreviewResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  onClose: () => void;
}

function PreviewModal({
  response,
  isLoading,
  isError,
  error,
  onClose,
}: PreviewModalProps): JSX.Element {
  // Fecha com ESC
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="email-preview-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-sara-bg border border-sara-border rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between gap-3 p-4 border-b border-sara-border">
          <div className="min-w-0 flex-1">
            <p id="email-preview-title" className="text-sm font-medium text-sara-text-muted">
              Assunto
            </p>
            <p className="text-base font-semibold text-sara-text truncate">
              {response?.subject ?? (isLoading ? 'carregando…' : '—')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-sara-text-muted hover:text-sara-text text-2xl leading-none px-2"
            aria-label="Fechar preview"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-hidden bg-white">
          {isLoading && <p className="p-6 text-sara-text-muted text-sm">renderizando…</p>}
          {isError && (
            <p role="alert" className="p-6 text-sara-danger text-sm">
              {error instanceof ApiError ? error.message : 'erro renderizando preview'}
            </p>
          )}
          {response && (
            <iframe
              title="Preview do email"
              srcDoc={response.html}
              sandbox=""
              className="w-full h-[70vh] border-0"
            />
          )}
        </div>
      </div>
    </div>
  );
}

function LinkConfigSection(): JSX.Element {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery<LinkConfig>({
    queryKey: ['link-config'],
    queryFn: () => request<LinkConfig>('/admin/links-config'),
  });

  const [form, setForm] = useState<Partial<LinkConfig>>({});

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const save = useMutation({
    mutationFn: (input: EditableLinkConfig) =>
      request<LinkConfig>('/admin/links-config', { method: 'PUT', body: input }),
    onSuccess: (fresh) => {
      queryClient.setQueryData(['link-config'], fresh);
      setForm(fresh);
    },
  });

  function update<K extends keyof LinkConfig>(k: K, v: string): void {
    setForm((prev) => ({ ...prev, [k]: v }));
  }

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    if (!form) return;
    const payload: EditableLinkConfig = {
      whatsappLink: form.whatsappLink ?? '',
      kiwifyMembersLink: form.kiwifyMembersLink ?? '',
      linkTiragemAvulsa: form.linkTiragemAvulsa ?? '',
      supportEmail: form.supportEmail ?? '',
      emailFromName: form.emailFromName ?? '',
      signatureName: form.signatureName ?? '',
      signatureRole: form.signatureRole ?? '',
      tiragemValorCentavos: form.tiragemValorCentavos ?? 0,
      moeda: form.moeda ?? 'BRL',
      frontCheckoutLink: form.frontCheckoutLink ?? '',
    };
    save.mutate(payload);
  }

  if (isLoading)
    return (
      <div className="card">
        <p className="text-sara-text-muted text-sm">carregando…</p>
      </div>
    );
  if (isError || !data)
    return (
      <div className="card">
        <p role="alert" className="text-sara-danger text-sm">
          erro carregando configurações
        </p>
      </div>
    );

  return (
    <form onSubmit={onSubmit} className="card space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Links e textos do email</h2>
        <p className="muted text-xs mt-1">
          Atualizado em {new Date(data.updatedAt).toLocaleString('pt-BR')}
          {data.updatedBy ? ` por ${data.updatedBy}` : ''}.
        </p>
      </div>

      <Field
        label="Link do WhatsApp"
        hint="Botão 'Falar com a taróloga' do email."
        value={form.whatsappLink ?? ''}
        onChange={(v) => update('whatsappLink', v)}
      />
      <Field
        label="Link da área de membros Kiwify"
        hint="Botão 'Acessar minha área de membros' do email."
        value={form.kiwifyMembersLink ?? ''}
        onChange={(v) => update('kiwifyMembersLink', v)}
      />
      <Field
        label="Link de pagamento da tiragem"
        hint="Link que a Sara manda quando cliente fora da janela pede uma tiragem específica. Cada compra vira +1 crédito de tiragem. (BR: Kiwify; EN: DigiStore.)"
        value={form.linkTiragemAvulsa ?? ''}
        onChange={(v) => update('linkTiragemAvulsa', v)}
      />

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="block text-sm font-medium text-sara-text">Valor da tiragem</span>
          <span className="block text-xs text-sara-text-subtle mb-1">
            Fonte da verdade: aparece no prompt da IA e na mensagem de cobrança. (BR: 11,77)
          </span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={
              form.tiragemValorCentavos != null ? (form.tiragemValorCentavos / 100).toFixed(2) : ''
            }
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                tiragemValorCentavos: Math.round((Number(e.target.value) || 0) * 100),
              }))
            }
            className="input text-sm w-full"
          />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-sara-text">Moeda</span>
          <span className="block text-xs text-sara-text-subtle mb-1">BR = BRL · Inglês = USD.</span>
          <select
            value={form.moeda ?? 'BRL'}
            onChange={(e) => update('moeda', e.target.value)}
            className="input text-sm w-full"
          >
            <option value="BRL">BRL (R$)</option>
            <option value="USD">USD ($)</option>
          </select>
        </label>
      </div>

      <Field
        label="Link de checkout do front (acesso inicial)"
        hint="Checkout do produto front. No BR fica vazio (vendido por fora); na instância EN é o link DigiStore do Energetic Seed."
        value={form.frontCheckoutLink ?? ''}
        onChange={(v) => update('frontCheckoutLink', v)}
      />
      <Field
        label="Email de suporte (responder pra cliente)"
        hint="Caixa que recebe replies. Usado tbm como `from` se não houver override."
        value={form.supportEmail ?? ''}
        onChange={(v) => update('supportEmail', v)}
        type="email"
      />
      <Field
        label="Nome do remetente do email"
        hint="Ex: 'Sara'. Aparece como 'Sara <contato@...>' na caixa de entrada."
        value={form.emailFromName ?? ''}
        onChange={(v) => update('emailFromName', v)}
      />
      <Field
        label="Assinatura — Nome"
        hint="Aparece no fechamento do email (ex: 'Vitória')."
        value={form.signatureName ?? ''}
        onChange={(v) => update('signatureName', v)}
      />
      <Field
        label="Assinatura — Cargo/Equipe"
        hint="Linha embaixo do nome (ex: 'Equipe Semente Energética')."
        value={form.signatureRole ?? ''}
        onChange={(v) => update('signatureRole', v)}
      />

      <div className="flex items-center gap-3 pt-2">
        <button
          type="submit"
          className="btn-primary text-sm"
          disabled={save.isPending}
          aria-busy={save.isPending}
        >
          {save.isPending ? 'Salvando…' : 'Salvar alterações'}
        </button>
        {save.isSuccess && (
          <span role="status" className="text-sara-success text-sm">
            ✓ salvo
          </span>
        )}
        {save.isError && (
          <span role="alert" className="text-sara-danger text-sm">
            {save.error instanceof ApiError ? save.error.message : 'erro ao salvar'}
          </span>
        )}
      </div>
    </form>
  );
}

interface FieldProps {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
}

function Field({ label, hint, value, onChange, type = 'text' }: FieldProps): JSX.Element {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-sara-text">{label}</span>
      {hint && <span className="block text-xs text-sara-text-subtle mb-1">{hint}</span>}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input text-sm w-full"
      />
    </label>
  );
}
